/**
 * @file iaComplianceApi.ts
 *
 * Data layer for the Internal Affairs compliance feature on the Station Admin
 * page: policy-PDF management (presigned upload / list / delete / one-shot
 * download-URL mint) and AI compliance runs (start / poll / history).
 *
 * All routes are served by the MCP agent API through aiClient's configured
 * agent API base URL. The backend derives the station from the WorkOS
 * authorizer's org_id, so no stationId is ever sent from the client.
 *
 * Every response is zod-`.parse()`d against the frozen shared contract: a
 * backend drift (renamed/removed field, unknown enum value) fails loudly at
 * the fetch boundary instead of silently rendering a broken card. The exported
 * types are derived from the schemas via z.infer, so the compile-time and
 * runtime contracts can't diverge.
 */
import { z } from "zod";
import aiClient from "@/shared/api/clients/aiClient";
import presignedClient from "@/shared/api/clients/presignedClient";

// ---------------------------------------------------------------------------
// Contract constants (mirror the server; the server is authoritative)
// ---------------------------------------------------------------------------

/** Server cap per policy PDF (20MB — base64 must fit the model request cap). */
export const MAX_POLICY_DOC_BYTES = 20_971_520;
/** Server cap on reports per compliance run. */
export const MAX_RUN_REPORTS = 20;
/** Server cap on policy docs per compliance run. */
export const MAX_RUN_POLICY_DOCS = 10;
/** Server cap on the combined size of the selected policy docs per run. */
export const MAX_RUN_POLICY_DOC_BYTES = MAX_POLICY_DOC_BYTES;
/** The only content type the upload contract accepts. */
export const POLICY_DOC_CONTENT_TYPE = "application/pdf";

// ---------------------------------------------------------------------------
// Schemas / types
// ---------------------------------------------------------------------------

export const PolicyDocSchema = z.object({
  docId: z.string(),
  fileName: z.string(),
  sizeBytes: z.number(),
  uploadedBy: z.string(),
  createdAt: z.string(),
});
export type PolicyDoc = z.infer<typeof PolicyDocSchema>;

const UploadInitResponseSchema = z.object({
  docId: z.string(),
  uploadUrl: z.string(),
  s3Key: z.string(),
  expiresIn: z.number(),
});

const UploadCompleteResponseSchema = z.object({ doc: PolicyDocSchema });
const ListPolicyDocsResponseSchema = z.object({
  docs: z.array(PolicyDocSchema),
});
const DeletePolicyDocResponseSchema = z.object({ deleted: z.literal(true) });
const DownloadUrlResponseSchema = z.object({
  downloadUrl: z.string(),
  expiresIn: z.number(),
});
export type PolicyDocDownloadUrl = z.infer<typeof DownloadUrlResponseSchema>;

export const ComplianceFindingSchema = z.object({
  severity: z.enum(["violation", "warning", "info"]),
  policyReference: z.string(),
  requirement: z.string(),
  evidence: z.string(),
  explanation: z.string(),
});

export const ComplianceResultSchema = z.object({
  reportId: z.string(),
  title: z.string().optional(),
  status: z.enum(["ok", "error"]),
  compliant: z.boolean().optional(),
  confidence: z.enum(["high", "medium", "low"]).optional(),
  summary: z.string().optional(),
  findings: z.array(ComplianceFindingSchema).optional(),
  error: z.string().optional(),
  checkedAt: z.string(),
});
export type ComplianceResult = z.infer<typeof ComplianceResultSchema>;

const CanonicalTimestampSchema = z.iso
  .datetime({ offset: true })
  .transform((value) => new Date(value).toISOString());

export const ComplianceRunSchema = z.object({
  runId: z.string(),
  status: z.enum(["queued", "running", "completed", "failed"]),
  requestedBy: z.string(),
  policyDocNames: z.array(z.string()),
  reportCount: z.number(),
  checkedCount: z.number(),
  violationCount: z.number(),
  // Rolling deployments and legacy persisted rows may omit the new partial-
  // failure count; absence means no recorded check errors.
  errorCount: z.number().default(0),
  createdAt: CanonicalTimestampSchema,
  updatedAt: CanonicalTimestampSchema,
  completedAt: z.string().optional(),
  error: z.string().optional(),
});
export type ComplianceRun = z.infer<typeof ComplianceRunSchema>;
export type ComplianceRunStatus = ComplianceRun["status"];

const StartRunResponseSchema = z.object({ runId: z.string() });
const GetRunResponseSchema = z.object({
  run: ComplianceRunSchema,
  results: z.array(ComplianceResultSchema),
});
export type ComplianceRunDetail = z.infer<typeof GetRunResponseSchema>;
const ListRunsResponseSchema = z.object({
  runs: z.array(ComplianceRunSchema),
  nextCursor: z.string().min(1).optional(),
});
export type ComplianceRunsPage = z.infer<typeof ListRunsResponseSchema>;

export interface ComplianceRunReportInput {
  reportId: string;
  /** Display-only; the server clips it to 200 chars. */
  title?: string;
  narrativeOnly?: boolean;
}

export interface StartComplianceRunRequest {
  reports: ComplianceRunReportInput[];
  policyDocIds: string[];
}

// ---------------------------------------------------------------------------
// Policy documents
// ---------------------------------------------------------------------------

export async function listPolicyDocs(
  signal?: AbortSignal,
): Promise<PolicyDoc[]> {
  const response = await aiClient.get("/ia-policy-docs", { signal });
  return ListPolicyDocsResponseSchema.parse(response.data).docs;
}

export async function deletePolicyDoc(docId: string): Promise<void> {
  const response = await aiClient.delete(
    `/ia-policy-docs/${encodeURIComponent(docId)}`,
  );
  DeletePolicyDocResponseSchema.parse(response.data);
}

/**
 * Mints a short-lived presigned GET URL for viewing a policy PDF. Each mint
 * writes an immutable CJIS audit row server-side BEFORE the URL is signed, so
 * callers must treat this as ONE-SHOT — one intentional open == one mint ==
 * one audit row. Never re-mint on a timer (phantom "accesses" the admin never
 * made — same rationale as useCustomFilePreview).
 */
export async function getPolicyDocDownloadUrl(
  docId: string,
): Promise<PolicyDocDownloadUrl> {
  const response = await aiClient.get(
    `/ia-policy-docs/${encodeURIComponent(docId)}/download-url`,
  );
  return DownloadUrlResponseSchema.parse(response.data);
}

/**
 * Client-side mirror of the server's upload-init validation, so obviously
 * unacceptable files get a friendly message without a round trip. The server
 * remains authoritative (it re-checks all three, plus magic bytes on
 * completion).
 *
 * @returns a user-facing error message, or null when the file is acceptable.
 */
export function validatePolicyDocFile(file: File): string | null {
  if (!file.name.toLowerCase().endsWith(".pdf")) {
    return "Policy documents must be PDF files (.pdf).";
  }
  // Some platforms report an empty MIME type for PDFs; the upload itself
  // always declares application/pdf, so only reject a concrete mismatch.
  if (file.type && file.type !== POLICY_DOC_CONTENT_TYPE) {
    return "Policy documents must be PDF files (.pdf).";
  }
  if (file.size <= 0) {
    return "This file is empty.";
  }
  if (file.size > MAX_POLICY_DOC_BYTES) {
    return "Policy documents must be 20MB or smaller.";
  }
  return null;
}

/**
 * Full policy-doc upload flow: upload-init → presigned S3 PUT → upload-complete.
 *
 * The PUT goes through presignedClient (no auth interceptor — an Authorization
 * header breaks the query-string signature) and sends EXACTLY the Content-Type
 * declared at init, because the presigned URL signs it; any drift is a 403
 * SignatureDoesNotMatch.
 */
export async function uploadPolicyDoc(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<PolicyDoc> {
  const initResponse = await aiClient.post("/ia-policy-docs/upload-init", {
    fileName: file.name,
    sizeBytes: file.size,
    contentType: POLICY_DOC_CONTENT_TYPE,
  });
  const init = UploadInitResponseSchema.parse(initResponse.data);

  await presignedClient.put(init.uploadUrl, file, {
    headers: { "Content-Type": POLICY_DOC_CONTENT_TYPE },
    onUploadProgress: (e) => {
      if (e.total && onProgress) {
        onProgress((e.loaded / e.total) * 100);
      }
    },
  });

  const completeResponse = await aiClient.post(
    "/ia-policy-docs/upload-complete",
    { docId: init.docId },
  );
  return UploadCompleteResponseSchema.parse(completeResponse.data).doc;
}

// ---------------------------------------------------------------------------
// Compliance runs
// ---------------------------------------------------------------------------

export async function startComplianceRun(
  req: StartComplianceRunRequest,
): Promise<{ runId: string }> {
  const response = await aiClient.post("/ia-compliance/run", req);
  return StartRunResponseSchema.parse(response.data);
}

export async function getComplianceRun(
  runId: string,
  signal?: AbortSignal,
): Promise<ComplianceRunDetail> {
  const response = await aiClient.get(
    `/ia-compliance/runs/${encodeURIComponent(runId)}`,
    { signal },
  );
  return GetRunResponseSchema.parse(response.data);
}

export async function listComplianceRunsPage(
  cursor?: string,
  signal?: AbortSignal,
): Promise<ComplianceRunsPage> {
  const response = await aiClient.get(
    "/ia-compliance/runs",
    cursor ? { params: { cursor }, signal } : { signal },
  );
  return ListRunsResponseSchema.parse(response.data);
}

/** The newest page used by the on-screen Check History list. */
export async function listComplianceRuns(
  signal?: AbortSignal,
): Promise<ComplianceRun[]> {
  return (await listComplianceRunsPage(undefined, signal)).runs;
}

/**
 * Walk every server page for the explicit history-export action. Run IDs are
 * deduplicated defensively in case rows move while a long export is paging;
 * a repeated cursor is rejected rather than allowing a malformed response to
 * spin forever.
 */
export async function listAllComplianceRuns(
  signal?: AbortSignal,
): Promise<ComplianceRun[]> {
  const runs: ComplianceRun[] = [];
  const runIds = new Set<string>();
  const cursors = new Set<string>();
  let cursor: string | undefined;

  do {
    const page = await listComplianceRunsPage(cursor, signal);
    for (const run of page.runs) {
      if (runIds.has(run.runId)) continue;
      runIds.add(run.runId);
      runs.push(run);
    }

    cursor = page.nextCursor;
    if (cursor) {
      if (cursors.has(cursor)) {
        throw new Error("Compliance history returned a repeated cursor");
      }
      cursors.add(cursor);
    }
  } while (cursor);

  return runs;
}
